/**
 * A card's journey, on a screen of its own.
 *
 * It began as a section on the card's page and had to leave. A journey is
 * something you go and look at, not something that sits between the price and
 * everything else pushing the page longer for the many cards that have no story
 * to tell. The owner: "It needs to be its own screen, though. It can't take up
 * space on card detail."
 *
 * So the card page carries a button and this holds the timeline, with room to
 * name the card it belongs to and to say something useful when the journey is
 * still empty, which the inline version could only do by hiding itself.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Route as RouteIcon } from 'lucide-react';

import CardJourney from '../components/tradeNow/CardJourney';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import haptics from '../utils/haptics';

export default function TradeJourney() {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cardId) return undefined;
    let alive = true;
    supabase
      .from('cards')
      .select('id, name, set_name, set_number, digital_image_url, tcgdex_image, original_image_url')
      .eq('id', cardId)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return;
        setCard(data || null);
        setLoading(false);
      });
    return () => { alive = false; };
  }, [cardId]);

  const s = makeStyles(theme);
  const art = card?.digital_image_url || card?.tcgdex_image || card?.original_image_url || null;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button
          style={s.back}
          onClick={() => { haptics.tap(); navigate(-1); }}
          aria-label="Back to the card"
        >
          <ChevronLeft size={22} />
        </button>
        <div style={s.headerTitle}>Trade Journey</div>
        <span style={s.headerSpacer} />
      </div>

      <div style={s.scroll}>
        {/* Which card this is about. On its own screen there is no card above
            it any more, so it has to say. */}
        {card && (
          <div style={s.card}>
            {art && <img src={art} alt={card.name} style={s.art} />}
            <div style={s.cardText}>
              <div style={s.cardName}>{card.name}</div>
              <div style={s.cardSet}>
                {[card.set_name, card.set_number ? `#${card.set_number}` : null].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
        )}

        <CardJourney cardId={cardId} theme={theme} titleStyle={s.sectionTitle} hideWhenEmpty={false} />

        {!loading && !card && (
          <div style={s.empty}>
            <RouteIcon size={26} color={theme.textMuted} />
            <div style={s.emptyTitle}>That card is not here</div>
            <div style={s.emptyBody}>It may have been traded away or removed.</div>
          </div>
        )}
      </div>
    </div>
  );
}

const makeStyles = (theme) => ({
  page: {
    position: 'fixed',
    inset: 0,
    backgroundColor: theme.bg,
    color: theme.text,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 900,
    paddingTop: 'var(--safe-area-top, 0px)',
    paddingBottom: 'var(--safe-area-bottom, 0px)',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderBottom: `1px solid ${theme.border}`,
    flex: '0 0 auto',
  },
  back: {
    width: 40,
    height: 40,
    borderRadius: 12,
    border: 'none',
    backgroundColor: 'transparent',
    color: theme.text,
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    padding: 0,
  },
  headerTitle: { fontSize: 17, fontWeight: '800', textAlign: 'center', color: theme.text },
  headerSpacer: { width: 40 },
  scroll: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 16px 40px' },
  card: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    padding: 12,
    borderRadius: 14,
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
  },
  art: { width: 48, height: 67, objectFit: 'cover', borderRadius: 6, flex: '0 0 auto' },
  cardText: { minWidth: 0 },
  cardName: { fontSize: 16, fontWeight: '700', color: theme.text },
  cardSet: { fontSize: 12, color: theme.textSecondary, marginTop: 2 },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.textMuted,
    marginBottom: 10,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    textAlign: 'center',
    padding: '48px 24px',
  },
  emptyTitle: { fontSize: 16, fontWeight: '700', color: theme.text },
  emptyBody: { fontSize: 13, color: theme.textSecondary, lineHeight: 1.5, maxWidth: 280 },
});
